import React, { useEffect, useMemo, useState } from 'react'
import useTranslation from '../../../i18n/useTranslation'
import './changelog.css'

const PLACEHOLDER_SHEET_URL_PATTERN = /REPLACE_WITH_YOUR_KEY/i
const CHANGELOG_SHEET_ID = '1DkvSQkl76cZlVp8LeeUG83Znc9pyn7Wc7KMBuGaIsmQ'
const CHANGELOG_PUBLISHED_CSV_URL =
  'https://docs.google.com/spreadsheets/d/e/2PACX-1vRnzrXECZZeD2Cb-MsN5CX0WtxykSB0hrGEn3_bP-IgxIBQwxUGrCTkA5H-juXC898dhq33FuOmBnQ_/pub?gid=0&single=true&output=csv'

const sanitizeCsvUrl = (value) => {
  const trimmed = (value || '').trim()
  if (!trimmed) return ''
  // Recover URL when env was pasted as "REACT_APP_...=https://..."
  if (trimmed.includes('REACT_APP_') && trimmed.includes('http')) {
    const match = trimmed.match(/https?:\/\/\S+/)
    return match ? match[0] : ''
  }
  return trimmed.startsWith('http') ? trimmed : ''
}

const getChangelogEditUrl = () => {
  const fromEnv = (process.env.REACT_APP_CHANGELOG_SHEET_EDIT_URL || '').trim()
  if (fromEnv.startsWith('http')) return fromEnv
  return `https://docs.google.com/spreadsheets/d/${CHANGELOG_SHEET_ID}/edit?gid=0`
}

const getChangelogCsvUrl = () => {
  const fromEnv = sanitizeCsvUrl(process.env.REACT_APP_CHANGELOG_CSV_URL)
  if (fromEnv && !PLACEHOLDER_SHEET_URL_PATTERN.test(fromEnv)) {
    return fromEnv
  }
  return CHANGELOG_PUBLISHED_CSV_URL
}

const isHtmlResponse = (text) => /^\s*</.test(text || '')

const ALL_ENVS = [
  'liveprep-admin',
  'liveprep-student',
  'liveprep-parent',
  'liveprep-educator'
]

const ALL_TYPES = ['feature', 'bug fix', 'improvement']

const ENV_TONE_KEYS = {
  'liveprep-admin': 'admin',
  'liveprep-student': 'student',
  'liveprep-parent': 'parent',
  'liveprep-educator': 'educator'
}

function parseCsv(text) {
  const rows = []
  let row = []
  let field = ''
  let q = false
  let i = 0
  while (i < text.length) {
    const c = text[i]
    if (q) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i += 2
          continue
        }
        q = false
        i++
        continue
      }
      field += c
      i++
      continue
    }
    if (c === '"') {
      q = true
      i++
      continue
    }
    if (c === ',') {
      row.push(field)
      field = ''
      i++
      continue
    }
    if (c === '\r') {
      i++
      continue
    }
    if (c === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
      i++
      continue
    }
    field += c
    i++
  }
  if (field.length || row.length) {
    row.push(field)
    rows.push(row)
  }
  return rows.filter((r) => r.some((cell) => cell && cell.length))
}

function csvToObjects(text) {
  const rows = parseCsv(text)
  if (!rows.length) return []
  const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, '_'))
  return rows.slice(1).map((r) =>
    Object.fromEntries(headers.map((h, idx) => [h, (r[idx] || '').trim()]))
  )
}

function normalizeEntry(raw, idx, getTypeMeta) {
  const type = (raw.type || '').toLowerCase().trim()
  const envField = raw.environments || raw.environment || raw.env || ''
  const environments = envField
    .split(/[,;|]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
  const dateRaw = raw.date || raw.shipped || raw.released_on || ''
  const date = dateRaw ? new Date(dateRaw) : null
  return {
    id: idx,
    type,
    typeMeta: getTypeMeta(type, raw.type),
    title: raw.title || raw.headline || '(untitled)',
    summary: raw.summary || raw.description || raw.body || '',
    dateRaw,
    date: date && !isNaN(date.getTime()) ? date : null,
    environments,
    clickup: raw.clickup || raw.clickup_url || raw.url || raw.link || '',
    assignedTo: raw.assigned_to || raw.assignee || raw.owner || ''
  }
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const fmtDate = (d) =>
  d ? `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}` : ''
const monthKey = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}` : 'z-undated'

const Ic = {
  Spark: (p) => (
    <svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor' {...p}>
      <path d='M8 0l1.6 4.7L14 6.3l-4.4 1.6L8 12.6 6.4 7.9 2 6.3l4.4-1.6L8 0zm5.5 9.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z' />
    </svg>
  ),
  Bug: (p) => (
    <svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor' {...p}>
      <path d='M11 4a3 3 0 10-6 0H4a1 1 0 100 2h.1A4 4 0 004 7v1H2a1 1 0 100 2h2v1c0 .3 0 .7.1 1H2a1 1 0 100 2h2.7A4 4 0 008 16a4 4 0 003.3-2H14a1 1 0 100-2h-2.1c.1-.3.1-.7.1-1v-1h2a1 1 0 100-2h-2V7c0-.3 0-.7-.1-1H12a1 1 0 100-2h-1z' />
    </svg>
  ),
  Up: (p) => (
    <svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor' {...p}>
      <path d='M8 2a1 1 0 01.7.3l4 4a1 1 0 11-1.4 1.4L9 5.4V13a1 1 0 11-2 0V5.4L4.7 7.7A1 1 0 113.3 6.3l4-4A1 1 0 018 2z' />
    </svg>
  ),
  External: (p) => (
    <svg
      viewBox='0 0 16 16'
      width='12'
      height='12'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
      {...p}
    >
      <path d='M6 3H4a1 1 0 00-1 1v8a1 1 0 001 1h8a1 1 0 001-1v-2M10 3h3v3M13 3L7 9' />
    </svg>
  ),
  Search: (p) => (
    <svg
      viewBox='0 0 16 16'
      width='14'
      height='14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      {...p}
    >
      <circle cx='7' cy='7' r='4.5' />
      <path d='M10.5 10.5L14 14' />
    </svg>
  ),
  Refresh: (p) => (
    <svg
      viewBox='0 0 16 16'
      width='14'
      height='14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
      {...p}
    >
      <path d='M14 8a6 6 0 11-1.8-4.3' />
      <path d='M14 2v4h-4' />
    </svg>
  ),
  Empty: (p) => (
    <svg
      viewBox='0 0 64 64'
      width='64'
      height='64'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
      {...p}
    >
      <rect x='10' y='14' width='44' height='40' rx='6' />
      <path d='M10 24h44M18 32h12M18 40h22' />
      <circle cx='46' cy='40' r='6' stroke='currentColor' />
      <path d='M50 44l4 4' />
    </svg>
  ),
  Sheet: (p) => (
    <svg
      viewBox='0 0 16 16'
      width='14'
      height='14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
      {...p}
    >
      <rect x='2.5' y='2.5' width='11' height='11' rx='1.5' />
      <path d='M2.5 6.5h11M2.5 10.5h11M6 2.5v11' />
    </svg>
  )
}

const Changelog = ({ csvUrl }) => {
  const { translate } = useTranslation()
  const url = csvUrl || getChangelogCsvUrl()
  const sheetEditUrl = getChangelogEditUrl()

  const getTypeMeta = (type, rawType) => {
    const map = {
      feature: { label: translate('changelogTool.typeFeature'), tone: 'feature' },
      'bug fix': { label: translate('changelogTool.typeBugFix'), tone: 'bugfix' },
      bugfix: { label: translate('changelogTool.typeBugFix'), tone: 'bugfix' },
      bug: { label: translate('changelogTool.typeBugFix'), tone: 'bugfix' },
      improvement: { label: translate('changelogTool.typeImprovement'), tone: 'improvement' }
    }
    return map[type] || { label: rawType || translate('changelogTool.typeUpdate'), tone: 'improvement' }
  }

  const getEnvLabel = (env) => {
    const key = ENV_TONE_KEYS[env]
    if (!key) return env
    return translate(`changelogTool.env${key.charAt(0).toUpperCase()}${key.slice(1)}`)
  }

  const [state, setState] = useState({ status: 'loading', entries: [], error: null })
  const [typeFilter, setTypeFilter] = useState(new Set(ALL_TYPES))
  const [envFilter, setEnvFilter] = useState(new Set(ALL_ENVS))
  const [query, setQuery] = useState('')

  const load = () => {
    setState({ status: 'loading', entries: [], error: null })
    fetch(url, { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then((text) => {
        if (isHtmlResponse(text)) {
          throw new Error('SHEET_NOT_PUBLIC')
        }
        const objs = csvToObjects(text)
        const entries = objs
          .map((raw, idx) => normalizeEntry(raw, idx, getTypeMeta))
          .filter((e) => e.title && e.title !== '(untitled)')
        entries.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))
        setState({ status: 'ready', entries, error: null })
      })
      .catch((err) =>
        setState({ status: 'error', entries: [], error: err.message || String(err) })
      )
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  const stats = useMemo(() => {
    const s = { total: 0, feature: 0, 'bug fix': 0, improvement: 0 }
    for (const e of state.entries) {
      s.total++
      if (e.type === 'feature') s.feature++
      else if (e.type === 'bug fix' || e.type === 'bug' || e.type === 'bugfix') s['bug fix']++
      else if (e.type === 'improvement') s.improvement++
    }
    return s
  }, [state.entries])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return state.entries.filter((e) => {
      const t = e.type === 'bug' || e.type === 'bugfix' ? 'bug fix' : e.type
      if (!typeFilter.has(t)) return false
      if (e.environments.length === 0) {
        if (envFilter.size !== ALL_ENVS.length) return false
      } else if (!e.environments.some((env) => envFilter.has(env))) {
        return false
      }
      if (q) {
        const hay = `${e.title} ${e.summary} ${e.assignedTo}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [state.entries, typeFilter, envFilter, query])

  const groups = useMemo(() => {
    const labelForDate = (d) =>
      d
        ? `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
        : translate('changelogTool.undated')
    const m = new Map()
    for (const e of filtered) {
      const k = monthKey(e.date)
      if (!m.has(k)) m.set(k, { key: k, label: labelForDate(e.date), entries: [] })
      m.get(k).entries.push(e)
    }
    return [...m.values()].sort((a, b) => (a.key < b.key ? 1 : -1))
  }, [filtered, translate])

  const toggle = (setter, current, value) => {
    const next = new Set(current)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    setter(next)
  }
  const setOnly = (setter, value) => setter(new Set([value]))
  const setAll = (setter, all) => setter(new Set(all))

  const filtersActive =
    typeFilter.size !== ALL_TYPES.length ||
    envFilter.size !== ALL_ENVS.length ||
    query.length > 0

  const clearFilters = () => {
    setAll(setTypeFilter, ALL_TYPES)
    setAll(setEnvFilter, ALL_ENVS)
    setQuery('')
  }

  const typeChips = [
    { key: 'feature', tone: 'feature', label: translate('changelogTool.typeFeature') },
    { key: 'bug fix', tone: 'bugfix', label: translate('changelogTool.typeBugFix') },
    { key: 'improvement', tone: 'improvement', label: translate('changelogTool.typeImprovement') }
  ]

  return (
    <div className='zlog'>
      <header className='zlog-head'>
        <div>
          <h1 className='zlog-h1'>{translate('changelogTool.title')}</h1>
          <p className='zlog-sub'>{translate('changelogTool.subtitle')}</p>
        </div>
        <div className='zlog-head-actions'>
          <a className='zlog-btn zlog-btn-tertiary' href={sheetEditUrl} target='_blank' rel='noopener noreferrer'>
            <Ic.Sheet /> {translate('changelogTool.sourceSheet')}
          </a>
          <button
            type='button'
            className='zlog-btn zlog-btn-primary'
            onClick={load}
            disabled={state.status === 'loading'}
          >
            <Ic.Refresh /> {translate('changelogTool.refresh')}
          </button>
        </div>
      </header>

      <section className='zlog-stats'>
        <StatCard label={translate('changelogTool.totalReleases')} value={stats.total} tone='logo' />
        <StatCard label={translate('changelogTool.features')} value={stats.feature} tone='feature' />
        <StatCard label={translate('changelogTool.bugFixes')} value={stats['bug fix']} tone='bugfix' />
        <StatCard
          label={translate('changelogTool.improvements')}
          value={stats.improvement}
          tone='improvement'
        />
      </section>

      <section className='zlog-filters'>
        <div className='zlog-filter-row'>
          <span className='zlog-filter-label'>{translate('changelogTool.filterType')}</span>
          <Chip
            active={typeFilter.size === ALL_TYPES.length}
            onClick={() => setAll(setTypeFilter, ALL_TYPES)}
          >
            {translate('changelogTool.all')}
          </Chip>
          {typeChips.map(({ key, tone, label }) => (
            <Chip
              key={key}
              tone={tone}
              active={typeFilter.has(key) && typeFilter.size !== ALL_TYPES.length}
              onClick={() => {
                if (typeFilter.size === ALL_TYPES.length) setOnly(setTypeFilter, key)
                else toggle(setTypeFilter, typeFilter, key)
              }}
            >
              {label}
            </Chip>
          ))}
        </div>

        <div className='zlog-filter-row'>
          <span className='zlog-filter-label'>{translate('changelogTool.filterEnvironment')}</span>
          <Chip
            active={envFilter.size === ALL_ENVS.length}
            onClick={() => setAll(setEnvFilter, ALL_ENVS)}
          >
            {translate('changelogTool.all')}
          </Chip>
          {ALL_ENVS.map((env) => {
            const tone = ENV_TONE_KEYS[env]
            return (
              <Chip
                key={env}
                tone={`env-${tone}`}
                active={envFilter.has(env) && envFilter.size !== ALL_ENVS.length}
                onClick={() => {
                  if (envFilter.size === ALL_ENVS.length) setOnly(setEnvFilter, env)
                  else toggle(setEnvFilter, envFilter, env)
                }}
              >
                {getEnvLabel(env)}
              </Chip>
            )
          })}
          <div className='zlog-search'>
            <Ic.Search />
            <input
              type='text'
              placeholder={translate('changelogTool.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type='button'
                className='zlog-search-clear'
                onClick={() => setQuery('')}
                aria-label={translate('changelogTool.clearSearch')}
              >
                ×
              </button>
            )}
          </div>
        </div>
      </section>

      <div className='zlog-result-meta'>
        {state.status === 'ready' && (
          <>
            <span>
              {translate('changelogTool.showing', {
                filtered: filtered.length,
                total: state.entries.length
              })}
            </span>
            {filtersActive && (
              <button type='button' className='zlog-link' onClick={clearFilters}>
                {translate('changelogTool.clearFilters')}
              </button>
            )}
          </>
        )}
      </div>

      {state.status === 'loading' && <LoadingState translate={translate} />}
      {state.status === 'error' && (
        <ErrorState
          message={state.error}
          onRetry={load}
          url={url}
          sheetEditUrl={sheetEditUrl}
          translate={translate}
        />
      )}
      {state.status === 'ready' && filtered.length === 0 && (
        <EmptyState
          hasEntries={state.entries.length > 0}
          onClear={clearFilters}
          translate={translate}
        />
      )}

      {state.status === 'ready' &&
        groups.map((g) => (
          <section key={g.key} className='zlog-group'>
            <div className='zlog-group-head'>
              <span className='zlog-group-bar' />
              <h2 className='zlog-group-label'>{g.label}</h2>
              <span className='zlog-group-count'>
                {g.entries.length}{' '}
                {g.entries.length === 1
                  ? translate('changelogTool.updateSingular')
                  : translate('changelogTool.updatePlural')}
              </span>
            </div>
            <div className='zlog-list'>
              {g.entries.map((e) => (
                <Entry key={e.id} entry={e} getEnvLabel={getEnvLabel} translate={translate} />
              ))}
            </div>
          </section>
        ))}
    </div>
  )
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`zlog-stat zlog-stat--${tone}`}>
      <div className='zlog-stat-value'>{value}</div>
      <div className='zlog-stat-label'>{label}</div>
    </div>
  )
}

function Chip({ children, active, onClick, tone }) {
  return (
    <button
      type='button'
      className={`zlog-chip ${active ? 'is-active' : ''} ${tone ? `zlog-chip--${tone}` : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function TypeBadge({ meta }) {
  return (
    <span className={`zlog-type zlog-type--${meta.tone}`}>
      <span className='zlog-type-ic'>
        {meta.tone === 'feature' && <Ic.Spark />}
        {meta.tone === 'bugfix' && <Ic.Bug />}
        {meta.tone === 'improvement' && <Ic.Up />}
      </span>
      {meta.label}
    </span>
  )
}

function EnvTag({ env, getEnvLabel }) {
  const tone = ENV_TONE_KEYS[env]
  if (!tone) {
    return <span className='zlog-env zlog-env--unknown'>{env}</span>
  }
  return (
    <span className={`zlog-env zlog-env--${tone}`}>
      <span className='zlog-env-dot' /> {getEnvLabel(env)}
    </span>
  )
}

function Entry({ entry, getEnvLabel, translate }) {
  return (
    <article className='zlog-entry'>
      <div className='zlog-entry-main'>
        <div className='zlog-entry-row1'>
          <TypeBadge meta={entry.typeMeta} />
          <h3 className='zlog-entry-title'>{entry.title}</h3>
        </div>
        {entry.summary && <p className='zlog-entry-summary'>{entry.summary}</p>}
        <div className='zlog-entry-meta'>
          <div className='zlog-envs'>
            {entry.environments.length > 0 ? (
              entry.environments.map((env) => <EnvTag key={env} env={env} getEnvLabel={getEnvLabel} />)
            ) : (
              <span className='zlog-env zlog-env--unknown'>
                {translate('changelogTool.noEnvironment')}
              </span>
            )}
          </div>
          <div className='zlog-entry-foot'>
            {entry.assignedTo && (
              <span className='zlog-owner'>
                <span className='zlog-owner-avatar' aria-hidden>
                  {initials(entry.assignedTo)}
                </span>
                <span>{entry.assignedTo}</span>
              </span>
            )}
            {entry.clickup && (
              <a className='zlog-clickup' href={entry.clickup} target='_blank' rel='noopener noreferrer'>
                {translate('changelogTool.clickup')} <Ic.External />
              </a>
            )}
          </div>
        </div>
      </div>
      <div className='zlog-entry-date'>{fmtDate(entry.date) || entry.dateRaw || '—'}</div>
    </article>
  )
}

function initials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
}

function LoadingState({ translate }) {
  return (
    <div className='zlog-state'>
      <div className='zlog-spinner' />
      <p>{translate('changelogTool.loading')}</p>
    </div>
  )
}

function ErrorState({ message, onRetry, url, sheetEditUrl, translate }) {
  const notPublic = message === 'SHEET_NOT_PUBLIC'
  return (
    <div className='zlog-state zlog-state--error'>
      <div className='zlog-state-title'>{translate('changelogTool.errorTitle')}</div>
      <p className='zlog-state-body'>
        {notPublic
          ? translate('changelogTool.errorNotPublic')
          : `${message}. ${translate('changelogTool.errorBody')}`}
      </p>
      {!notPublic && <code className='zlog-state-url'>{url}</code>}
      <a className='zlog-btn zlog-btn-tertiary' href={sheetEditUrl} target='_blank' rel='noopener noreferrer'>
        <Ic.Sheet /> {translate('changelogTool.openSheet')}
      </a>
      <button type='button' className='zlog-btn zlog-btn-primary' onClick={onRetry}>
        <Ic.Refresh /> {translate('changelogTool.tryAgain')}
      </button>
    </div>
  )
}

function EmptyState({ hasEntries, onClear, translate }) {
  return (
    <div className='zlog-state'>
      <div className='zlog-empty-icon' aria-hidden>
        <Ic.Empty />
      </div>
      <div className='zlog-state-title'>
        {hasEntries ? translate('changelogTool.emptyFiltered') : translate('changelogTool.emptySheet')}
      </div>
      <p className='zlog-state-body'>
        {hasEntries ? translate('changelogTool.emptyFilteredBody') : translate('changelogTool.emptySheetBody')}
      </p>
      {hasEntries && (
        <button type='button' className='zlog-btn zlog-btn-tertiary' onClick={onClear}>
          {translate('changelogTool.clearFilters')}
        </button>
      )}
    </div>
  )
}

export default Changelog
